// Dátum-megjelenítés egy helyen, mindig helyi időben.
//
// A backend kétféle dátum-stringet küld:
//   - "2026-08-06T14:00:00"        - views `datetime_plain` formatter, amit a
//                                    view már a site időzónájára váltott
//   - "2024-10-07T12:29:14+00:00"  - nyers mezőérték explicit eltolással
// A `new Date()` az elsőt helyi időként, a másodikat az eltolás alapján olvassa,
// tehát mindkettőből a helyes időpillanat lesz.
//
// Amit tilos: `toISOString()`-gel formázni. Az UTC-re vált, és ezzel elcsúsztatja
// a naptári napot minden olyan időpontnál, ahol a helyi óra kisebb az időzóna
// eltolásánál - CEST-ben tehát 00:00 és 02:00 között. Ez nem elméleti: a
// 2021-10-01T00:00 határidejű Európa Kollégium pályázatnál a felület
// 2021.09.30-át írt ki. Lásd #97.

const pad = value => String(value).padStart(2, "0");

/**
 * Date objektummá alakít, üres/érvénytelen értékre null-t ad.
 */
export const parseDate = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // A csak dátumot tartalmazó stringet ("2024-10-07") a JS UTC éjfélként
  // olvassa, nem helyiként - negatív eltolású időzónában ez egy nappal
  // korábbi napot adna. Helyi éjfélként építjük fel.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Táblázatokban használt formátum: "2024. 03. 15."
 */
export const formatDate = value => {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleDateString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Határidő-mondatokban használt tömör formátum: "2026.08.06"
 */
export const formatDateCompact = value => {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
};

/**
 * Beküldéshez használt "YYYY-MM-DD", a helyi naptári nap szerint.
 *
 * Szándékosan nem `toISOString().split("T")[0]`: az UTC-re váltana, és
 * CEST-ben egy helyi éjfélre eső dátumot az előző napként küldene be.
 */
export const formatDateIso = value => {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Óra:perc helyi időben: "14:00"
 */
export const formatTime = value => {
  const date = parseDate(value);
  if (!date) return "";
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
